import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Copy, Trash2 } from 'lucide-react';
import { useBuilderStore } from '@/stores/builderStore';
import { BlockRenderer } from './BlockRenderer';
import { cn } from '@mawkaa/ui';
import type { Block } from '@mawkaa/types';

interface SortableBlockProps {
  block: Block;
}

function SortableBlock({ block }: SortableBlockProps) {
  const {
    selectedBlockId,
    hoveredBlockId,
    isPreviewMode,
    selectBlock,
    hoverBlock,
    duplicateBlock,
    removeBlock,
  } = useBuilderStore();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isSelected = selectedBlockId === block.id;
  const isHovered = hoveredBlockId === block.id;

  if (isPreviewMode) {
    return <BlockRenderer block={block} />;
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'block-wrapper relative group',
        isDragging && 'opacity-50',
        isSelected && 'selected',
        isHovered && !isSelected && 'ring-2 ring-primary/30'
      )}
      onClick={() => selectBlock(block.id)}
      onMouseEnter={() => hoverBlock(block.id)}
      onMouseLeave={() => hoverBlock(null)}
    >
      {/* Block toolbar */}
      <div
        className={cn(
          'absolute -top-10 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-white rounded-lg shadow-lg p-1 z-10 transition-opacity',
          isSelected || isHovered ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
      >
        <button
          {...attributes}
          {...listeners}
          className="p-1.5 hover:bg-gray-100 rounded cursor-grab"
          title="Déplacer"
        >
          <GripVertical className="w-4 h-4 text-gray-500" />
        </button>
        <button
          className="p-1.5 hover:bg-gray-100 rounded"
          onClick={(e) => {
            e.stopPropagation();
            duplicateBlock(block.id);
          }}
          title="Dupliquer"
        >
          <Copy className="w-4 h-4 text-gray-500" />
        </button>
        <button
          className="p-1.5 hover:bg-red-100 rounded"
          onClick={(e) => {
            e.stopPropagation();
            removeBlock(block.id);
          }}
          title="Supprimer"
        >
          <Trash2 className="w-4 h-4 text-red-500" />
        </button>
      </div>

      {/* Block content */}
      <BlockRenderer block={block} isEditing />
    </div>
  );
}

export function BuilderCanvas() {
  const { blocks, isPreviewMode, addBlock } = useBuilderStore();

  if (blocks.length === 0) {
    return (
      <div className="min-h-[400px] flex items-center justify-center border-2 border-dashed border-gray-200 rounded-lg m-4">
        <div className="text-center">
          <p className="text-muted-foreground mb-2">
            Glissez un bloc ici pour commencer
          </p>
          <button
            onClick={() => addBlock('hero')}
            className="text-sm text-primary hover:underline"
          >
            ou cliquez pour ajouter un bloc Hero
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[400px]">
      {blocks.map((block) => (
        <SortableBlock key={block.id} block={block} />
      ))}
    </div>
  );
}
